import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import * as msisdn from './formConfigActivationMsisdn';
import * as tariff from './formConfigActivationTariff';
import * as topup from '../form/steps/formConfigTopup';
import * as payment from './formConfigActivationPayment';
import * as personal from './formConfigActivationPersonal';
import * as confirm from './formConfigActivationConfirm';
import { bindParamsToRoute, bindQueryParams } from '../../helpers/url';
import { confirmOrder } from '../../actions/order/completed';
import {
  BRAND_ROSSMANN,
  FORM_FIELD_SIM_STATUS,
  QUERY_ORDER_ID,
  QUERY_IDENT_PROVIDER,
  QUERY_IDENT_METHOD,
  HEADER_KEY_TRANSACTION_ID,
  FUNNEL_ID_ACTIVATION,
} from '../../helpers/constants';
import { EECC_ACTIVATION_CONFIRM } from '../../config/api';
import FormManager from '../form/FormManager';
import GlobalSection from '../../components/basics/global/GlobalSection';
import { send } from '../../actions/request/send';
import EECCActivationInitiateRequest from '../../model/requests/EECCActivationInitiateRequest';
import { selectFunnelByProcess } from '../../selectors/funnel';

const steps = [
  msisdn,
  { ...tariff, next: topup.id },
  { ...topup, next: personal.id },
  { ...personal, next: payment.id },
  { ...payment, next: confirm.id },
  confirm,
];

class ActivationForm extends PureComponent {
  constructor(...args) {
    super(...args);
    this.onBeforeSubmit = this.onBeforeSubmit.bind(this);
    this.onAfterSubmitSuccess = this.onAfterSubmitSuccess.bind(this);
    this.getSubmitRoute = this.getSubmitRoute.bind(this);
    this.getSuccessRoute = this.getSuccessRoute.bind(this);
    this.onStepEnter = this.onStepEnter.bind(this);
    this.sendInitiateRequest = this.sendInitiateRequest.bind(this);
    this.createRequestBody = this.createRequestBody.bind(this);
  }

  onBeforeSubmit(fieldMap, normalizedValues, formValues) {
    const { consentVersion, funnelId } = this.props;

    const finalizedValues = {
      contract_summary_confirmed: true,
      consent: {
        ad_consent: formValues.ad_consent,
        pass_data: formValues.pass_data,
        contract_terms_accepted: true,
        contract_terms_version: consentVersion || undefined,
        funnelId: funnelId || undefined,
      },
    };

    return finalizedValues;
  }

  onAfterSubmitSuccess({ response, responseBody }) {
    const { dispatch } = this.props;
    const orderId = response.headers.get(HEADER_KEY_TRANSACTION_ID);
    dispatch(confirmOrder(
      orderId,
      {
        contractId: responseBody.clientContractId,
        response: responseBody,
      },
    ));
  }

  async onStepEnter(step, fieldMap, formValues) {
    const { dispatch } = this.props;

    if (step === 'confirm') {
      const requestBody = this.createRequestBody(formValues);

      await dispatch(send(new EECCActivationInitiateRequest(requestBody)));
    }
  }

  /**
   * Called By the form handler to determine where the form should
   * be submitted to.
   *
   * @param {Object} fieldMap
   * @param {Object} finalizedValues
   * @param {Object} formValues - original form values
   * @returns {string}
   */
  getSubmitRoute(fieldMap, finalizedValues, formValues) {
    const { processId } = this.props;
    return bindParamsToRoute(EECC_ACTIVATION_CONFIRM, { msisdn: formValues.msisdn, processId });
  }

  getSuccessRoute({ response, responseBody }) {
    const { brandName, sitemap } = this.props;
    const orderId = response.headers.get(HEADER_KEY_TRANSACTION_ID);
    if (brandName === BRAND_ROSSMANN) {
      return bindQueryParams(
        sitemap.RossmannSimActivationSuccessRoute.url,
        {
          [QUERY_ORDER_ID]: orderId,
          [QUERY_IDENT_PROVIDER]: responseBody.deferredIdentification.identProvider,
          [QUERY_IDENT_METHOD]: responseBody.deferredIdentification.method,
        },
      );
    }

    return bindQueryParams(
      sitemap.SimActivationSuccessRoute.url,
      {
        [QUERY_ORDER_ID]: orderId,
        [QUERY_IDENT_PROVIDER]: responseBody.deferredIdentification.identProvider,
        [QUERY_IDENT_METHOD]: responseBody.deferredIdentification.method,
      },
    );
  }

  sendInitiateRequest(formValues) {
    const { dispatch } = this.props;
    const requestBody = this.createRequestBody(formValues);
    dispatch(send(new EECCActivationInitiateRequest(requestBody)));
  }

  createRequestBody(formValues) {
    const { bId, pplus } = this.props;
    const simStatus = formValues[FORM_FIELD_SIM_STATUS];

    const preparePaymentOptions = () => {

      switch (formValues.payment_type) {
        case '01': {
          return {
            type: 'debit',
            iban: formValues.media_id.replaceAll(' ', ''),
            owner: `${formValues.first_name} ${formValues.last_name}`,
          };
        }
        case '04': {
          return {
            type: 'cc',
            owner: formValues.credit_card_holder,
            provider: formValues.provider,
            registration_id: formValues.media_id,
            expiration_date: formValues.expiration_date,
            shortened_credit_card_number: formValues.shortened_credit_card_number,
          };
        }
        case '10': {
          return {
            type: 'paypal',
            paypal_transaction_id: 'paypalTransactionId', // klären
          };
        }
      }
    };

    const getTopup = () => {
      const result = {};
      switch (formValues._topup_type) {
        case 'single': {
          result.single_topup_amount = formValues._topup_amount;
          break;
        }
        case 'auto': {
          result.auto_topup = {};
          result.auto_topup.amount = formValues._topup_amount;

          if (formValues._topup_auto_type === 'balance') {
            result.auto_topup.type = 'ACCOUNT_BALANCE';
            result.auto_topup.trigger = formValues._topup_auto_trigger;

          } else if (formValues._topup_auto_type === 'monthly') {

            if (formValues._topup_auto_monthly_type === 'D') {
              result.auto_topup.type = 'DAY_OF_MONTH';
            } else {
              result.auto_topup.type = 'ONCE_PER_MONTH';
            }
          }
        }
      }
      return result;
    };

    const requestBody = {
      msisdn: formValues.msisdn,
      preactive_card_authentication: {
        activation_code: parseInt(formValues.sim_number, 10),
      },
      product: {
        tariff_identifier: simStatus.tariff.id,
      },
      customer: {
        gender: formValues.salutation === '01' ? 'MALE' : 'FEMALE',
        first_name: formValues.first_name,
        last_name: formValues.last_name,
        address: {
          street: formValues.street,
          house_number: formValues.house_number,
          zip: formValues.zip,
          city: formValues.city,
          supplement: formValues.address_supplement,
        },
        birth_date: formValues.birthday,
        email: formValues.email,
        password: formValues.password,
      },
      order_context: {
        retailer_id: bId,
        pplus: pplus || false,
        market: 'PUA',
      },
      deferred_identification: {
        method: formValues.ident_method,
        medium: formValues.id_card_type,
        nationality: formValues.id_card_nationality,
      },
      itemised_bill: {
        type: 'NO',
        subscription_type: 'DISABLED',
      },
    };

    if (formValues.payment_type) {
      requestBody.payment = preparePaymentOptions();
    }
    if (formValues._topup_type !== 'none') {
      requestBody.topup = { ...getTopup() };
    }
    return requestBody;
  }

  render() {
    const { location } = this.props;
    const stepProps = { ...this.props, context: topup.CONTEXT_SIM_ACTIVATION };
    const { headline } = this.props.params;

    return (
      <GlobalSection>
        <FormManager
          form={ActivationForm.formName}
          submitRoute={this.getSubmitRoute}
          successRoute={this.getSuccessRoute}
          onBeforeSubmit={this.onBeforeSubmit}
          onAfterSubmitSuccess={this.onAfterSubmitSuccess}
          onDataCorrection={this.sendInitiateRequest}
          headline={headline}
          steps={steps}
          onStepEnter={this.onStepEnter}
          location={location}
          stepProps={stepProps}
          className="ActivationForm"
          withProgressBar
          withBlockError
          withFooter
        />
      </GlobalSection>
    );
  }
}

/**
 * required by tracking!
 */
ActivationForm.formName = 'activation';

ActivationForm.propTypes = {
  location: PropTypes.object.isRequired,
  sitemap: PropTypes.object.isRequired,
  brandName: PropTypes.string,
  params: PropTypes.shape({
    urlSuccess: PropTypes.string.isRequired,
    headline: PropTypes.string.isRequired,
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
  pplus: PropTypes.object,
  consentVersion: PropTypes.string,
  funnelId: PropTypes.string,
  bId: PropTypes.string,
  processId: PropTypes.string,
};

const mapStateToProps = ({ site, user, funnel }) => ({
  brandName: site.brandName,
  sitemap: site.sitemap,
  consentVersion: site.consentVersion,
  funnelId: selectFunnelByProcess({ funnel }, FUNNEL_ID_ACTIVATION),
  pplus: user.pplus,
  orderByAgent: user.orderByAgent,
  processId: user.EECC.activation.processId,
  bId: user.bId,
});

const mapDispatchToProps = (dispatch) => ({
  dispatch,
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ActivationForm);
